import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  Pagination
} from '@mui/material'
import AppModal from '../common/AppModal'
import AppButton from '../buttons/AppButton'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import NotificationUserCard from './NotificationUserCard'
import {
  getUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '../../redux/actions/notificationActions'
import AppCircularProgress from '../common/AppCircularProgress'
import { useAppDispatch, useAppSelector } from '../../redux/store'

interface NotificationUserModalProps {
  open: boolean
  onClose: () => void
}

const NotificationUserModal: React.FC<NotificationUserModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch()
  const [onlyUnread, setOnlyUnread] = useState<boolean>(true)
  const [page, setPage] = useState<number>(1)

  const {
    items: notifications,
    loadingFetchAll: loadingNotifications,
    unreadCount,
    pagination
  } = useAppSelector((state) => state.notification)

  useEffect(() => {
    if (open) {
      dispatch(getUserNotifications({ onlyUnread, page, size: 5 }))
    }
  }, [dispatch, open, onlyUnread, page])

  return (
    <AppModal open={open} onClose={onClose} title="My Notifications" width="sm">
      <Stack
        sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              color="secondary"
              checked={onlyUnread}
              onChange={(e) => {
                setOnlyUnread(e.target.checked)
                setPage(1)
              }}
            />
          }
          label={<Typography variant="caption">Unread Only</Typography>}
        />
        {unreadCount > 0 && (
          <AppButton
            size="small"
            variant="text"
            color="secondary"
            startIcon={<DoneAllIcon />}
            onClick={() => dispatch(markAllNotificationsAsRead())}
          >
            Mark all as read
          </AppButton>
        )}
      </Stack>
      <Divider />

      <Box sx={{ minHeight: 300, maxHeight: 500, overflowY: 'auto' }}>
        {loadingNotifications ? (
          <AppCircularProgress />
        ) : notifications && notifications.length > 0 ? (
          notifications.map((n) => (
            <NotificationUserCard
              key={n.id}
              notification={n}
              onRead={(id) => dispatch(markNotificationAsRead(id))}
            />
          ))
        ) : (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary' }}>
              {onlyUnread ? 'No unread notifications.' : 'No notifications found.'}
            </Typography>
          </Box>
        )}
      </Box>

      {pagination && pagination.totalPages! > 1 && (
        <Stack sx={{ alignItems: 'center', pt: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Pagination
            size="small"
            color="secondary"
            count={pagination.totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
          />
        </Stack>
      )}
    </AppModal>
  )
}

export default NotificationUserModal
